import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { useFetch } from "../../lib/useFetch";
import { cn } from "../../lib/utils";
import { extractFromMultiName } from "../Locations/utils";

/**
 * Link an imported booking to its yacht & base. Candidates are ranked by the
 * API: yacht AND operator matching the deal first, then yacht name only, then
 * similar names. Several confident rows means the operator runs more than one
 * boat with that name — the reason those bookings weren't auto-linked.
 *
 * The search filters by source (CYA / Yachtfolio / NauSYS / BM / HELM), a
 * Yachtfolio brochure link can be added as a new yacht on the spot, and once a
 * yacht is linked ops/finance can convert the shell into a full booking page
 * (offer + yacht record, money prefilled from HubSpot, payments as per Xero).
 */

export type YachtLinkCandidate = {
  yachtId: string;
  yachtName: string;
  provider?: string | null;
  operatorId: string | null;
  operatorName: string | null;
  locationId: string | null;
  locationNames: string | null;
  boardingAtBase: number;
  confidence: "confident" | "name_match" | "similar";
};

type CandidatesResponse = {
  dealYacht: string;
  supplier: string;
  linkedYachtId: string | null;
  linkMethod: string | null;
  candidates: YachtLinkCandidate[];
};

const CONFIDENCE: Record<YachtLinkCandidate["confidence"], { label: string; className: string }> = {
  confident: { label: "yacht + operator match", className: "bg-green-100 text-green-800" },
  name_match: { label: "yacht name match", className: "bg-amber-100 text-amber-800" },
  similar: { label: "similar name", className: "bg-slate-100 text-slate-600" },
};

const PROVIDERS: { id: string; label: string }[] = [
  { id: "", label: "All sources" },
  { id: "CYA", label: "CYA" },
  { id: "Yachtfolio", label: "Yachtfolio" },
  { id: "NS", label: "NauSYS" },
  { id: "BM", label: "Booking Manager" },
  { id: "Helm", label: "HELM" },
];
const PROVIDER_LABEL: Record<string, string> = { CYA: "CYA", Yachtfolio: "Yachtfolio", NS: "NauSYS", BM: "Booking Manager", Helm: "HELM", Ankor: "Ankor" };

export function LinkYachtDialog({
  bookingId,
  canConvert = false,
  onClose,
  onLinked,
  onConverted,
}: {
  bookingId: string;
  /** ops/finance: offer "Convert to full booking" once a yacht is linked. */
  canConvert?: boolean;
  onClose: () => void;
  onLinked: () => void;
  onConverted?: () => void;
}) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [provider, setProvider] = useState("");
  const [data, setData] = useState<CandidatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [posting, setPosting] = useState<string | null>(null);
  const [yfUrl, setYfUrl] = useState("");
  const [yfBusy, setYfBusy] = useState(false);
  const [justLinked, setJustLinked] = useState<{ yachtId: string; yachtName: string } | null>(null);
  const [converting, setConverting] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (provider) params.set("provider", provider);
    const qs = params.toString();
    return fetch<{ data: CandidatesResponse }>(`/api/bookings/${bookingId}/yacht-link-candidates${qs ? `?${qs}` : ""}`)
      .then((r) => setData(r.data))
      .catch((e: any) => setError(e?.message ?? "Couldn't load yachts."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, q, provider]);

  const linked = (yachtId: string, yachtName: string, operatorName?: string | null) => {
    toast({ title: "Yacht linked", description: [yachtName, operatorName].filter(Boolean).join(" · ") });
    setPosting(null);
    if (canConvert) {
      // Keep the dialog open so the next step — the full booking page — is one click away.
      setJustLinked({ yachtId, yachtName });
      setData((d) => (d ? { ...d, linkedYachtId: yachtId } : d));
      load();
    } else onLinked();
  };

  const link = (c: YachtLinkCandidate) => {
    setPosting(c.yachtId);
    setError(null);
    fetch(`/api/bookings/${bookingId}/link-yacht`, "POST", { yachtId: c.yachtId })
      .then(() => linked(c.yachtId, c.yachtName, c.operatorName))
      .catch((e: any) => {
        setError(e?.message ?? "Couldn't link the yacht.");
        setPosting(null);
      });
  };

  const addYachtfolio = () => {
    if (!yfUrl.trim()) return;
    setYfBusy(true);
    setError(null);
    fetch<{ data: { yachtId: string; name: string; existing?: boolean } }>(`/api/bookings/${bookingId}/yachtfolio-yacht`, "POST", { url: yfUrl.trim() })
      .then((r) => {
        setYfUrl("");
        linked(r.data.yachtId, r.data.name);
      })
      .catch((e: any) => setError(e?.message ?? "Couldn't add that Yachtfolio yacht."))
      .finally(() => setYfBusy(false));
  };

  const convert = () => {
    setConverting(true);
    setError(null);
    fetch<{ data: { converted: boolean; reason?: string; pricing?: { charterFee: number; currency: string }; schedule?: { written?: number }; xero?: any } }>(`/api/bookings/${bookingId}/convert-to-full`, "POST", {})
      .then((r) => {
        const d = r.data;
        toast({
          title: d.converted ? "Now a full booking" : "Already a full booking",
          description: d.converted ? `Money prefilled from HubSpot${d.pricing ? ` (${d.pricing.currency} ${d.pricing.charterFee.toLocaleString()})` : ""}; ${d.schedule?.written ?? 0} instalment(s) from the deal; Xero payments applied.` : d.reason,
        });
        (onConverted ?? onLinked)();
      })
      .catch((e: any) => setError(e?.message ?? "Couldn't convert this booking."))
      .finally(() => setConverting(false));
  };

  const confidentCount = (data?.candidates ?? []).filter((c) => c.confidence === "confident").length;
  const busy = !!posting || yfBusy || converting;
  const linkedId = justLinked?.yachtId ?? data?.linkedYachtId ?? null;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && (justLinked ? onLinked() : onClose())}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Link yacht & base</DialogTitle>
        </DialogHeader>
        {data && (
          <p className="text-sm text-slate-500">
            The deal says <span className="font-medium text-slate-700">{data.dealYacht || "no yacht"}</span>
            {data.supplier && (
              <>
                {" "}
                with <span className="font-medium text-slate-700">{data.supplier}</span>
              </>
            )}
            .
          </p>
        )}
        {justLinked && canConvert && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-300 bg-green-50 px-3 py-2">
            <div className="text-sm">
              <p className="font-medium text-green-900">{justLinked.yachtName} linked.</p>
              <p className="text-green-800">Convert to a full booking page: offer + yacht record, price / VAT / APA and instalments from the HubSpot deal, payments applied as Xero has them. Nothing is posted to Xero or HubSpot.</p>
            </div>
            <Button size="sm" disabled={busy} onClick={convert}>{converting ? "Converting…" : "Convert to full booking"}</Button>
          </div>
        )}
        {confidentCount > 1 && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {confidentCount} boats match both the yacht and the operator — check the base before linking.
          </p>
        )}
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <div className="flex flex-wrap gap-1.5">
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setProvider(p.id)}
              className={cn("rounded-full border px-2.5 py-0.5 text-xs", provider === p.id ? "border-blue-600 bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}
            >
              {p.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={provider ? `Search ${PROVIDERS.find((p) => p.id === provider)?.label} yachts…` : "Search another yacht name…"}
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
        />
        <div className="space-y-2">
          {loading && !data ? (
            <p className="text-sm text-slate-400">Finding yachts…</p>
          ) : !data?.candidates.length ? (
            <p className="text-sm text-slate-400">No {provider ? `${PROVIDERS.find((p) => p.id === provider)?.label} ` : ""}yachts match — try a different name{provider !== "Yachtfolio" ? "" : ", or paste the brochure link below"}.</p>
          ) : (
            data.candidates.map((c) => (
              <div
                key={c.yachtId}
                className={cn(
                  "flex items-center justify-between gap-3 rounded-lg border px-3 py-2",
                  linkedId === c.yachtId && "border-green-400 bg-green-50/50",
                )}
              >
                <div className="min-w-0">
                  <p className="font-medium">
                    {c.yachtName}
                    <span className={cn("ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium", CONFIDENCE[c.confidence].className)}>
                      {CONFIDENCE[c.confidence].label}
                    </span>
                    {c.provider && <span className="ml-1.5 rounded-full border px-2 py-0.5 text-[11px] text-slate-500">{PROVIDER_LABEL[c.provider] ?? c.provider}</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {[c.operatorName ?? "no operator", c.locationNames ? extractFromMultiName(c.locationNames) : "no base"].join(" · ")}
                    {" · "}
                    {c.boardingAtBase > 0 ? (
                      <span className="text-green-700">boarding info at this base</span>
                    ) : (
                      <span className="text-slate-400">no boarding info at this base</span>
                    )}
                  </p>
                </div>
                {linkedId === c.yachtId ? (
                  <span className="text-xs font-medium text-green-700">Linked</span>
                ) : (
                  <Button size="sm" disabled={busy} onClick={() => link(c)}>
                    {posting === c.yachtId ? "Linking…" : "Link"}
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
        <div className="rounded-lg border border-dashed px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Yacht not in the fleet yet?</p>
          <p className="mt-0.5 text-xs text-slate-500">Paste the Yachtfolio brochure link — the yacht is added (not public) and linked to this booking.</p>
          <div className="mt-2 flex gap-2">
            <input
              type="url"
              value={yfUrl}
              onChange={(e) => setYfUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addYachtfolio()}
              placeholder="https://www.yachtfolio.com/brochure/…"
              className="min-w-0 flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-blue-400"
            />
            <Button size="sm" variant="outline" disabled={busy || !yfUrl.trim()} onClick={addYachtfolio}>
              {yfBusy ? "Adding…" : "Add & link"}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => (justLinked ? onLinked() : onClose())}>
            {justLinked ? "Done" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
